import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import Highcharts from 'highcharts/esm/highcharts.src.js';

import type { Service } from 'webapp-types';
import { GoalStatus } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

export interface GoalStatusCount {
	status: GoalStatus;
	count: number;
}

export interface DonutArgs {
	data: GoalStatusCount[];
	showEmptyState?: boolean;
}

export default class DonutComponent extends Component<DonutArgs> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	chart: Highcharts.Chart | null = null;

	get emptyChartData() {
		return [4, 2, 2, 3].map((y) => ({
			color: '#8e929b', // --databox-neutral-400
			y,
			colorIndex: 99999,
		}));
	}

	get goalStatusCount() {
		return this.args.data;
	}

	get totalGoals() {
		return this.goalStatusCount.reduce((sum, { count }) => sum + count, 0);
	}

	get centerLabel() {
		return this.args.showEmptyState ? '-' : this.totalGoals;
	}

	get hasAnyGoals() {
		return this.totalGoals > 0;
	}

	get chartData() {
		const allStatuses = [
			GoalStatus.ON_TRACK,
			GoalStatus.AT_RISK,
			GoalStatus.OFF_TRACK,
			GoalStatus.OTHER,
		];
		const dataMap = new Map(this.goalStatusCount.map((item) => [item.status, item.count]));
		return allStatuses.map((status) => {
			const count = dataMap.get(status) || 0;
			const percent = this.hasAnyGoals ? Math.round((count / this.totalGoals) * 100) : 0;
			return this.prepareSliceData(status, count, percent);
		});
	}

	@action
	setNewData() {
		this.chart?.update(
			{
				series: [
					{
						type: 'pie',
						data: this.hasAnyGoals ? this.chartData : this.emptyChartData,
					},
				],
			},
			true,
			true,
			false,
		);
	}

	@action
	setupView(element: HTMLElement) {
		// bcos the context of 'this' changes inside Highcharts.chart scope
		const that = this;

		this.chart = Highcharts.chart(element, {
			chart: {
				type: 'pie',
				margin: [-10, -10, -10, -10],
				style: {
					fontFamily: '"Roboto", "Arial", "Trebuchet MS", sans-serif',
					fontWeight: 'bold',
				},
				events: {
					render() {
						// customCenterLabel: the label this donut draws in its center, kept to replace it on the next render
						const chart = this as Highcharts.Chart & {
							customCenterLabel?: Highcharts.SVGElement;
						};
						chart.customCenterLabel?.destroy();
						const centerX = chart.plotLeft + chart.plotWidth / 2;
						const centerY = chart.plotTop + (chart.plotHeight + 15) / 2;
						chart.customCenterLabel = chart.renderer
							.text(String(that.centerLabel), centerX, centerY)
							.attr({ 'text-anchor': 'middle' })
							.css({ color: 'var(--text-color-1)', fontSize: '24px' })
							.add();
					},
				},
			},
			title: { text: undefined },
			tooltip: { enabled: false },
			plotOptions: {
				pie: {
					innerSize: '65%',
					dataLabels: { enabled: false },
					borderRadius: 4,
					enableMouseTracking: false,
				},
			},
			series: [
				{
					type: 'pie',
					data: this.hasAnyGoals ? this.chartData : this.emptyChartData,
				},
			],
			credits: { enabled: false },
		});
	}

	@action
	teardownView() {
		this.chart?.destroy();
	}

	private prepareSliceData(status: GoalStatus, count: number, percent: number) {
		return {
			status,
			count,
			name: this.mmGoalsService.getStatusText(status),
			color: this.mmGoalsService.getStatusColor(status),
			class: this.mmGoalsService.getStatusClass(status),
			percent,
			y: count,
			colorIndex: 99999,
		};
	}
	<template>
		<div class="donut-container">
			<div
				class="donut-chart"
				{{didInsert this.setupView}}
				{{didUpdate this.setNewData this.chartData}}
				{{willDestroy this.teardownView}}
			></div>
			<div class="donut-legend">
				{{#each this.chartData as |item|}}
					<div class="donut-legend-item {{item.class}}">
						<span class="title">
							{{item.name}}
							{{#unless @showEmptyState}}
								<span>
									{{item.percent}}%
								</span>
							{{/unless}}
						</span>
						<span class="number">
							{{#if @showEmptyState}}
								-
							{{else}}
								{{item.count}}
							{{/if}}
						</span>
					</div>
				{{/each}}
			</div>
		</div>
	</template>
}
